import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import defaultFormState, { requiredFields } from '@models/address'

import * as addressActions from '@redux/modules/address'
import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import type { AddressModel, AddressRequestOptions } from '@models/types'

type CreateAddressParams = {
  addressParams: Partial<AddressModel>,
  dispatch: AppDispatch,
  requestOptions?: AddressRequestOptions,
}

const createAddress = (params: CreateAddressParams) => {
  const { dispatch, addressParams, requestOptions } = params
  const { createAddress: createFn } = addressActions

  return dispatch(createFn(addressParams, requestOptions))
}

type UpdateAddressParams = {
  address: AddressModel,
  addressParams: Partial<AddressModel>,
  dispatch: AppDispatch,
  requestOptions?: AddressRequestOptions,
}

const updateAddress = (params: UpdateAddressParams) => {
  const {
    dispatch, address, addressParams, requestOptions,
  } = params
  const { updateAddress: updateFn } = addressActions

  const updatedParams = {
    id: address.id,
    ...addressParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteAddressParams = {
  address: DeleteParams<AddressModel>,
  dispatch: AppDispatch,
}

const deleteAddress = (params: DeleteAddressParams) => {
  const { dispatch, address } = params
  const { deleteAddress: deleteFn } = addressActions

  return dispatch(deleteFn(address))
}

type ResetAddressParams = {
  agencyId: number,
}

type ResetDefaultAddressParams = {
  resetAddressParams: ResetAddressParams,
  dispatch: AppDispatch,
}

const resetDefaultAddress = (params: ResetDefaultAddressParams) => {
  // @ts-expect-error TS2339
  const { agencyId, dispatch } = params
  const { resetDefaultAddress: resetFn } = addressActions

  return dispatch(resetFn(agencyId))
}

type UpdateDefaultAddressParams = {
  addressParams: Partial<AddressModel>,
  dispatch: AppDispatch,
  requestOptions?: AddressRequestOptions,
}

const updateDefaultAddress = (params: UpdateDefaultAddressParams) => {
  const { addressParams, dispatch, requestOptions } = params
  const { updateDefaultAddress: updateFn } = addressActions

  // @ts-expect-error TS2345
  return dispatch(updateFn(addressParams, requestOptions))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAddressForm(
  address: Partial<AddressModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const addressForm = useForm(
    defaultFormState,
    { entity: address, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [address.id, address.cache_key],
  )

  return {
    ...addressForm,
  }
}

function useAddress(initEntity: Partial<AddressModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: address }: { entity: AddressModel} = useLatestEntity(initEntity, 'addresses')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.addresses)

  return {
    address,
    callbacks: {
      createAddress: (addressParams: Partial<AddressModel>, entityOptions?: AddressRequestOptions) => (
        createAddress({ addressParams, dispatch, requestOptions: entityOptions })
      ),
      deleteAddress: () => deleteAddress({ dispatch, address }),
      resetDefaultAddress: (agencyId: Number, entityOptions?: AddressRequestOptions) => (
        // @ts-expect-error TS2353
        resetDefaultAddress({ agencyId, dispatch, requestOptions: entityOptions })
      ),
      createOrEditAddress: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditAddressModal',
        payload: { address },
      }),
      updateAddress: (addressParams: Partial<AddressModel>, entityOptions?: AddressRequestOptions) => (
        updateAddress({
          address, addressParams, dispatch, requestOptions: entityOptions,
        })
      ),
      updateDefaultAddress: (
        addressParams: Partial<AddressModel>,
        entityOptions?: AddressRequestOptions,
      ) => updateDefaultAddress({ addressParams, dispatch, requestOptions: entityOptions }),
    },
    creating,
    deleting,
    loading: loading && !loadedIds.includes(address.id),
    updating,
  }
}

export type UseAddressPayload = ReturnType<typeof useAddress>

export default useAddress
